import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, RiskPill, StatusPill, PEPBadge, AtomBadge, CBNBadge, EmptyState } from "../components/primitives";
import { formatNGN, formatDate, formatDateTime, maskBVN } from "../lib/format";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Clock, History } from "lucide-react";

const TABS = ["Overview","Transactions","Risk profile","KYC & Documents","Beneficial ownership","Screening history","Cases & alerts","Time-travel","Audit trail"];

export default function CustomerDetail() {
  const { cid } = useParams();
  const [c, setC] = useState(null);
  const [txns, setTxns] = useState({ transactions: [], summary: {} });
  const [tab, setTab] = useState("Overview");
  const [showBvn, setShowBvn] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [scrubIdx, setScrubIdx] = useState(0);

  useEffect(() => {
    api.get(`/customers/${cid}`).then(r => setC(r.data));
    api.get(`/customers/${cid}/transactions?limit=80`).then(r => setTxns(r.data));
    api.get(`/timeline/customer/${cid}`).then(r => {
      setTimeline(r.data);
      setScrubIdx((r.data.events?.length || 1) - 1);
    });
  }, [cid]);

  const currentScrubEvent = useMemo(() => timeline?.events?.[scrubIdx] || null, [timeline, scrubIdx]);

  if (!c) return <div className="p-6 text-[13px] text-ink-muted">Loading customer…</div>;

  const riskHistory = Array.from({length:12}, (_,i) => ({ month: ["Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr","May"][i], score: Math.max(8, c.risk_score - 50 + i*4 + (i===11?12:0)) }));

  return (
    <div>
      <PageHeader
        title={c.name}
        subtitle={`${c.type} · ${c.id} · Banked since ${c.opening_date}`}
        sections={["5.2","5.3","5.4"]}
        atom={<AtomBadge model="Atom Neutron" version="v2.0" description="KYC retrieval & orchestration" />}
        right={<Link to="/atom-explainer" className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-sm hover:bg-navy-700">Open in Atom Explainer</Link>}
      />

      {/* Identity strip */}
      <div className="px-6 py-4 bg-bg-surface border-b border-line flex items-center gap-6 flex-wrap">
        <div className="w-14 h-14 rounded-sm bg-amber/15 border border-amber/30 flex items-center justify-center text-[18px] font-medium text-navy">
          {c.name.split(" ").map(s => s[0]).join("").slice(0,2)}
        </div>
        <div className="flex flex-wrap gap-6 flex-1">
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">BVN</div><div className="text-[13px] font-medium hash-mono flex items-center gap-1">{showBvn ? c.bvn : maskBVN(c.bvn)} <button onClick={() => setShowBvn(!showBvn)} className="text-[10px] text-amber-600 ml-1">{showBvn?'hide':'reveal'}</button></div></div>
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">NIN</div><div className="text-[13px] font-medium hash-mono">{c.nin}</div></div>
          {c.rc_number && <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">RC</div><div className="text-[13px] font-medium">{c.rc_number}</div></div>}
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">KYC Tier</div><div><span className="text-[12px] px-1.5 py-0.5 border border-line rounded">{c.kyc_tier}</span></div></div>
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">Risk</div><div><RiskPill band={c.risk_band} score={c.risk_score} /></div></div>
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">PEP</div><div>{c.is_pep ? <span className="text-[11px]">{c.pep_position}</span> : <span className="text-[11px] text-ink-subtle">Not flagged</span>}</div></div>
          <div><div className="text-[10px] text-ink-subtle uppercase tracking-wider">Sanctions</div><div><StatusPill status={c.sanctions_status} /></div></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-line bg-bg-surface">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.replace(/\s+/g,'-').toLowerCase()}`} onClick={() => setTab(t)} className={`px-3 py-2 text-[12px] border-b-2 -mb-px whitespace-nowrap ${tab===t?'border-amber text-ink font-medium':'border-transparent text-ink-muted hover:text-ink'}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-3">
        {tab === "Overview" && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="col-span-1">
              <CardHeader title="Demographic" />
              <div className="p-3 text-[12px] space-y-1.5">
                {[
                  ["Full name", c.name],["DOB", c.dob],["Gender", c.gender],["Marital", c.marital_status],
                  ["Address", c.address],["Phone", c.phone],["Email", c.email],["Occupation", c.occupation],
                  ["Income band", c.income_band],["Source of funds", c.source_of_funds],
                ].map(([k,v]) => <div key={k} className="flex justify-between gap-3 border-b border-line/50 py-1"><span className="text-ink-subtle">{k}</span><span className="text-right">{v}</span></div>)}
              </div>
            </Card>
            <Card className="col-span-1">
              <CardHeader title="Accounts" subtitle={`${c.accounts.length} accounts on file`} />
              <div>
                {c.accounts.map((a, i) => (
                  <div key={i} className="px-3 py-2 border-b border-line/50 last:border-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[12px] font-medium hash-mono">{a.account_no}</span>
                      <StatusPill status={a.status} />
                    </div>
                    <div className="text-[11px] text-ink-subtle">{a.type} · opened {a.opening_date}</div>
                    <div className="text-[13px] font-medium tabular mt-1">{a.currency === 'NGN' ? formatNGN(a.balance) : `${a.currency} ${a.balance.toLocaleString()}`}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="col-span-1">
              <CardHeader title="Atom risk summary" right={<AtomBadge model="Atom Neutron" version="v2.0" />} />
              <div className="p-3">
                <div className="text-[12px] leading-relaxed text-ink">
                  {c.name} is a {c.kyc_tier} {c.type === "Individual" ? "retail" : "corporate"} customer based in {c.address.split(",").slice(-2,-1)[0]?.trim() || c.state}, declared occupation {c.occupation}, banked since {c.opening_date}.
                  {c.typology && c.risk_band === "Very High" && ` Risk score has elevated to ${c.risk_score} over the past 30 days driven by activity matching the AMLTRIX ${c.typology} typology — see open alerts. Atom Proton v2.4 flagged this profile for analyst review on 22 May 2026.`}
                  {!c.typology && ` Current risk score is ${c.risk_score} (${c.risk_band}). No active typology matches; profile remains within the bank's normal monitoring tier.`}
                </div>
                <div className="text-[10px] text-ink-subtle mt-3">Generated by Atom Neutron v2.0 · 22 May 2026 14:33 WAT · Explainability 0.91</div>
              </div>
            </Card>
          </div>
        )}

        {tab === "Transactions" && (
          <Card>
            <CardHeader title="Last 90 days · transactions" subtitle={`Inbound ${formatNGN(txns.summary.inbound || 0, {short:true})} · Outbound ${formatNGN(txns.summary.outbound || 0, {short:true})} · ${txns.summary.count || 0} transactions`} />
            <div className="overflow-x-auto">
              <table className="dense w-full tabular">
                <thead><tr><th>Timestamp</th><th>Direction</th><th>Channel</th><th className="text-right">Amount</th><th>Counterparty</th><th>Bank</th><th>Narration</th><th>Risk</th><th>Status</th></tr></thead>
                <tbody>
                  {txns.transactions.slice(0,60).map(t => (
                    <tr key={t.id}>
                      <td className="text-[11px]">{formatDateTime(t.ts)}</td>
                      <td className="text-[11px]">{t.direction}</td>
                      <td className="text-[11px]">{t.channel}</td>
                      <td className="text-right">{t.currency === 'NGN' ? formatNGN(t.amount) : `${t.currency} ${t.amount.toLocaleString()}`}</td>
                      <td className="text-[12px]">{t.counterparty}</td>
                      <td className="text-[11px] text-ink-subtle">{t.counterparty_bank}</td>
                      <td className="text-[11px] text-ink-subtle truncate max-w-[180px]">{t.narration}</td>
                      <td><span className={`px-1.5 py-0.5 text-[10px] rounded ${t.risk_score>=80?'bg-risk-veryhigh/15 text-risk-veryhigh':t.risk_score>=60?'bg-risk-high/15 text-risk-high':t.risk_score>=40?'bg-risk-medium/15 text-risk-medium':'bg-risk-low/15 text-risk-low'}`}>{t.risk_score}</span></td>
                      <td><StatusPill status={t.alert_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {txns.transactions.length === 0 && <EmptyState message="No transactions in last 90 days." />}
            </div>
          </Card>
        )}

        {tab === "Risk profile" && (
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardHeader title="Score breakdown" right={<AtomBadge model="Atom Proton" version="v2.4" />} />
              <div className="p-3 space-y-2">
                {[
                  ["Base score (customer rating)", 24],
                  ["Velocity vs 90-day baseline", c.typology ? 18 : 4],
                  ["Counterparty risk", c.typology ? 12 : 3],
                  ["Geographic spread", c.typology ? 7 : 2],
                  ["Amount above 95th percentile", c.typology ? 6 : 1],
                  ["Channel anomaly", c.typology ? 4 : 1],
                  ["PEP / sanctions adjacency", c.is_pep ? 8 : 0],
                  ["Beneficiary added recently", c.typology ? 3 : 0],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-[11px] mb-0.5"><span>{k}</span><span className="tabular">+{v}</span></div>
                    <div className="h-1.5 bg-bg-subtle rounded-sm"><div className="h-full bg-navy rounded-sm" style={{width: `${Math.min(100, v*5)}%`}} /></div>
                  </div>
                ))}
                <div className="pt-2 mt-2 border-t border-line flex justify-between text-[12px] font-medium"><span>Total</span><span className="tabular">{c.risk_score}</span></div>
              </div>
            </Card>
            <Card>
              <CardHeader title="Historical risk score · 12 months" />
              <div className="p-3 h-[200px]">
                <ResponsiveContainer>
                  <LineChart data={riskHistory}>
                    <XAxis dataKey="month" tick={{fontSize:10,fill:'#5C5C57'}} axisLine={{stroke:'#E5E5E0'}} tickLine={false} />
                    <YAxis tick={{fontSize:10,fill:'#5C5C57'}} axisLine={false} tickLine={false} domain={[0,100]} />
                    <Tooltip contentStyle={{fontSize:11,border:'1px solid #E5E5E0',borderRadius:4}} />
                    <Line type="monotone" dataKey="score" stroke="#0B2B5C" strokeWidth={2} dot={{fill:'#F59A2D',r:3}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="p-3 border-t border-line text-[11px] text-ink-muted">
                Score change log: 22 May 2026 — Atom Proton elevated to {c.risk_score} ({c.typology ? `${c.typology} pattern detected` : 'periodic re-score'}). Prior month average: {c.risk_score - 8}.
              </div>
            </Card>
          </div>
        )}

        {tab === "KYC & Documents" && (
          <Card>
            <CardHeader title="KYC documents" subtitle={`Tier ${c.kyc_tier.split(' ')[1]} verification on file · Atom Neutron v2.0 retrieval`} right={<button className="px-2 py-1 text-[11px] bg-navy text-white rounded-sm">Upload document</button>} />
            <table className="dense w-full">
              <thead><tr><th>Document</th><th>Upload date</th><th>Expiry</th><th>Status</th><th>Verifier</th></tr></thead>
              <tbody>
                {[
                  ["Passport photograph","18 Mar 2018","-","Verified","Sofia Marchetti"],
                  ["Government ID (International passport)","18 Mar 2018","12 Jan 2028","Verified","Sofia Marchetti"],
                  ["BVN slip","18 Mar 2018","-","Verified","System (NIBSS)"],
                  ["NIN slip","02 Aug 2020","-","Verified","System (NIMC)"],
                  ["Utility bill — EKEDC","04 Mar 2026","04 Jun 2026","Verified","Sofia Marchetti"],
                  ["Signature card","18 Mar 2018","-","Verified","Branch officer"],
                  ...(c.type === "Limited company" ? [
                    ["CAC certificate","18 Mar 2018","-","Verified","Sofia Marchetti"],
                    ["MEMART","18 Mar 2018","-","Verified","Sofia Marchetti"],
                    ["Form CO7 - Directors","18 Mar 2018","-","Verified","Sofia Marchetti"],
                    ["Board resolution","18 Mar 2018","-","Verified","Sofia Marchetti"],
                  ] : [])
                ].map((row, i) => (
                  <tr key={i}>{row.map((cell, j) => <td key={j} className="text-[12px]">{j===3 ? <StatusPill status={cell} /> : cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {tab === "Beneficial ownership" && (
          <Card>
            <CardHeader title="Beneficial ownership tree" right={<AtomBadge model="Atom Nucleus" version="v3.5" description="BO discrepancy detection" />} />
            <div className="p-6">
              {c.type === "Limited company" ? (
                <>
                  <div className="text-[13px] font-medium">{c.name}</div>
                  <div className="text-[11px] text-ink-subtle mb-4">{c.rc_number} · Incorporated 14 Feb 2019</div>
                  <div className="ml-4 border-l border-line pl-4 space-y-2">
                    <div><div className="text-[12px] font-medium">Director · Adekunle Williams</div><div className="text-[11px] text-ink-subtle">BVN ••• ••• 7392 · 35% shareholding</div></div>
                    <div><div className="text-[12px] font-medium">Director · Olamide Sanusi</div><div className="text-[11px] text-ink-subtle">BVN ••• ••• 1184 · 25% shareholding</div></div>
                    <div><div className="text-[12px] font-medium">UBO · Adekunle Williams (declared)</div><div className="text-[11px] text-ink-subtle">25%+ ownership; declaration date 14 Feb 2019</div></div>
                  </div>
                  {c.typology === "T0703" && (
                    <div className="mt-4 border-l-2 border-risk-high pl-3 py-2 bg-risk-high/5">
                      <div className="text-[12px] font-medium text-risk-high">BO discrepancy detected by Atom Nucleus v3.5</div>
                      <div className="text-[11px] text-ink-muted mt-1">Network analysis identifies recurring inflows from federal procurement with downstream outflows tracing to an account controlled by a Senator flagged in the PEP register. Recommended action: invoke BVN/NIN cross-check against directors; demand updated BO declaration.</div>
                    </div>
                  )}
                </>
              ) : <EmptyState message="Beneficial ownership tree applies to corporate customers only." />}
            </div>
          </Card>
        )}

        {tab === "Screening history" && (
          <Card>
            <CardHeader title="Screening history" right={<div className="flex gap-1"><button className="px-2 py-1 text-[11px] bg-navy text-white rounded-sm">Run screening now</button><button className="px-2 py-1 text-[11px] border border-line rounded-sm">Schedule periodic</button></div>} />
            <table className="dense w-full">
              <thead><tr><th>Timestamp</th><th>Lists checked</th><th>Result</th><th>Score</th><th>Screened by</th></tr></thead>
              <tbody>
                {[
                  ["22 May 2026 06:00 WAT","OFAC, UN, EU, CBN, PEP","Clear","-","Atom Electron v1.1"],
                  ["18 Apr 2026 06:00 WAT","OFAC, UN, EU, CBN, PEP","Clear","-","Atom Electron v1.1"],
                  ["12 Mar 2026 06:00 WAT","OFAC, UN, EU, CBN, PEP","Clear","-","Atom Electron v1.1"],
                  ["18 Mar 2018 (Onboarding)","OFAC, UN, EU, CBN, PEP, Adverse Media","Clear","-","Sofia Marchetti"],
                ].map((r,i) => <tr key={i}>{r.map((cell,j) => <td key={j} className="text-[12px]">{cell}</td>)}</tr>)}
              </tbody>
            </table>
          </Card>
        )}

        {tab === "Cases & alerts" && (
          <Card>
            <CardHeader title="Cases and alerts on this customer" />
            <div className="p-3">
              {c.typology ? (
                <div className="border border-line rounded-sm p-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[12px] font-medium">Active alert · AMLTRIX {c.typology}</span>
                    <StatusPill status="In case" />
                  </div>
                  <div className="text-[11px] text-ink-subtle mt-1">Atom Proton v2.4 confidence: 0.94 · Escalated for MLRO review</div>
                </div>
              ) : <EmptyState message="No active alerts or cases on this customer." />}
            </div>
          </Card>
        )}

        {tab === "Time-travel" && (
          <Card>
            <CardHeader
              title="Time-travel mode"
              subtitle="Scrub back through customer history to see state at any point in time"
              right={<AtomBadge model="Atom Photon" version="v1.0" description="Audit chain reconstruction" />}
            />
            <div className="p-5">
              {!timeline ? <div className="text-[12px] text-ink-muted">Loading timeline…</div> : (
                <>
                  {/* Current scrubbed state card */}
                  <div className="bg-gradient-to-br from-amber/8 via-bg-warm to-white border border-line rounded-2xl p-5 mb-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Clock size={14} strokeWidth={1.5} className="text-amber-600" />
                        <span className="text-[10px] uppercase tracking-wider text-ink-subtle">Snapshot at</span>
                        <span className="display text-[15px] font-semibold tabular">{currentScrubEvent?.ts || "—"}</span>
                      </div>
                      <span className="text-[10px] text-ink-subtle">Event {scrubIdx + 1} of {timeline.events.length}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Risk score (then)</div>
                        <div className="display text-[26px] font-semibold tabular mt-1">{currentScrubEvent?.risk_score || 0}<span className="text-[12px] text-ink-subtle">/100</span></div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Risk band</div>
                        <div className="mt-2"><RiskPill band={
                          (currentScrubEvent?.risk_score || 0) > 80 ? "Very High" :
                          (currentScrubEvent?.risk_score || 0) > 60 ? "High" :
                          (currentScrubEvent?.risk_score || 0) > 35 ? "Medium" : "Low"
                        } score={currentScrubEvent?.risk_score || 0} /></div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Event</div>
                        <div className="text-[13px] font-medium mt-1.5">{currentScrubEvent?.title}</div>
                        <div className="text-[10px] text-ink-subtle">{currentScrubEvent?.kind}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Detail</div>
                        <div className="text-[11px] text-ink-muted mt-1">{currentScrubEvent?.detail}</div>
                      </div>
                    </div>
                  </div>

                  {/* Slider */}
                  <div className="mb-4">
                    <input
                      type="range"
                      min={0}
                      max={timeline.events.length - 1}
                      value={scrubIdx}
                      onChange={(e) => setScrubIdx(Number(e.target.value))}
                      className="w-full accent-amber"
                      data-testid="time-travel-slider"
                    />
                    <div className="flex items-center justify-between text-[10px] text-ink-subtle mt-1">
                      <span>{timeline.events[0]?.ts}</span>
                      <span>Drag to scrub through history</span>
                      <span>{timeline.events[timeline.events.length - 1]?.ts}</span>
                    </div>
                  </div>

                  {/* Timeline list */}
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-2">Full timeline · click any event to scrub</div>
                    {timeline.events.map((e, i) => (
                      <button
                        key={i}
                        onClick={() => setScrubIdx(i)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors text-left ${i === scrubIdx ? "bg-amber/10 border-amber/40" : "border-line/50 hover:bg-bg-hover"}`}
                      >
                        <span className="text-[10px] tabular text-ink-subtle w-24 shrink-0">{e.ts}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium tracking-wide ${
                          e.kind === "alert" ? "bg-risk-high/10 text-risk-high" :
                          e.kind === "case" ? "bg-amber/15 text-amber-600" :
                          e.kind === "screening" ? "bg-info/10 text-info" :
                          e.kind === "onboarding" ? "bg-risk-low/10 text-risk-low" :
                          "bg-bg-warm text-ink-muted"
                        }`}>{e.kind}</span>
                        <span className="text-[12px] flex-1 truncate">{e.title}</span>
                        <span className="text-[10px] tabular text-ink-muted">risk {e.risk_score}/100</span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 pt-3 border-t border-line text-[10px] text-ink-subtle inline-flex items-center gap-1.5">
                    <History size={11} strokeWidth={1.5} />
                    Reconstructed from the tamper-evident audit chain · Atom Photon v1.0
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {tab === "Audit trail" && (
          <Card>
            <CardHeader title="Customer audit trail" subtitle="Every action ever taken on this record" />
            <table className="dense w-full">
              <thead><tr><th>Timestamp</th><th>User</th><th>Action</th></tr></thead>
              <tbody>
                {[
                  ["22 May 2026 14:33 WAT","System (Atom Proton v2.4)","Risk score updated → " + c.risk_score],
                  ["22 May 2026 06:00 WAT","System (Atom Electron v1.1)","Periodic sanctions screening — clear"],
                  ["18 Apr 2026 11:24 WAT","Sofia Marchetti","Reviewed KYC documents — no action required"],
                  ["02 Aug 2020 09:42 WAT","Sofia Marchetti","NIN verification completed"],
                  ["18 Mar 2018 10:08 WAT","System","Customer onboarded · " + c.opening_date],
                ].map((r,i) => <tr key={i}>{r.map((cell,j) => <td key={j} className="text-[12px]">{cell}</td>)}</tr>)}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
